<script setup lang="ts">
import { Coverage } from '~/layers/forms/models'

import type { AsyncDataRequestStatus } from '#app'

interface Props {
  plan?: Omit<Coverage, 'periodicity'>
  quotationStatus?: AsyncDataRequestStatus
}

const props = defineProps<Props>()

const emits = defineEmits(['update:modelValue', 'selected'])
const { RemoteConfigurationStore, LocalConfigurationStore } = stores()

const company = computed(() =>
  LocalConfigurationStore.value?.associatedCompanies?.find(
    (c) => c.argentoInsuranceId === RemoteConfigurationStore.value?.emisor?.id
  )
)

const selected = () => {
  if (!props.plan) return
  const data = {
    data: Coverage.load({
      ...props.plan,
      argentoInsuranceCompanyId: company.value?.argentoInsuranceId
        ? +company.value?.argentoInsuranceId
        : null,
    }),
  }
  emits('selected', data)
}
</script>

<template>
  <CardComponent bg="light">
    <template #header>
      <template v-if="plan?.planName">
        <div class="text-center leading-tight">
          <span> — Plan — </span>
          <br />
          <span
            class="font-bold uppercase leading-none 'text-4xl"
            :class="{
              'text-2xl': plan.planName.length > 14,
              'text-3xl': plan.planName.length <= 14,
            }"
            >{{ plan.planName }}</span
          >
        </div>
      </template>
    </template>
    <div class="flex flex-col w-full gap-2 p-2">
      <ProcessingComponent
        v-if="quotationStatus === 'pending'"
        loading-message="Cotizando... Espere unos minutos."
      />
      <template v-else>
        <template v-if="!plan">
          <ImageComponent
            class="aspect-[16/5] object-cover px-8"
            :src="getCompanyLogo(company?.logoWhiteUrl)"
          />
          <PvDivider />
        </template>
        <div class="min-h-[7rem] flex items-center justify-center">
          <CoveragePrice
            v-if="plan"
            :amount="plan.grandTotal ?? 0"
            :periodicity="Coverage.getPeriodicityList().default?.value"
            class="my-4"
          />
        </div>
        <template v-if="plan">
          <PvButton @click="selected">
            <span class="text-md text-md py-2 font-bold uppercase">Contratar</span>
          </PvButton>
        </template>
      </template>

      <template v-if="plan?.details">
        <div class="flex flex-col items-stretch justify-start gap-4 py-5">
          <SeparatorHeader
            :text="
              (plan.details.title?.length ? plan.details.title : 'Especificaciones').toUpperCase()
            "
          />

          <div class="flex flex-col gap-2">
            <div
              v-if="plan?.details?.description?.filter((i) => i.type === 'NOTE').length"
              class="flex flex-col gap-2"
            >
              <template
                v-for="(item, i) in plan.details.description.filter((i) => i.type === 'NOTE')"
                :key="i"
              >
                <div
                  class="rounded-md border border-surface-200 py-2 text-center text-xs dark:border-surface-600"
                >
                  <span>{{ item.label }}</span>
                </div>
              </template>
            </div>
            <template
              v-for="(item, i) in plan?.details?.description?.filter(
                (i) => !i.type || i.type === 'ITEM'
              )"
              :key="i"
            >
              <PvDivider
                v-if="i"
                class="opacity-20"
              />
              <div>
                <div class="text-xs">
                  {{ item.label.charAt(0).toUpperCase() + item.label.slice(1).toLowerCase() }}
                </div>
                <div class="font-bold text-primary-200 text-base">{{ item.detail }}</div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </div>
  </CardComponent>
</template>
